import {
  Component,
  OnInit,
  Input,
  ViewChild,
  TemplateRef,
  OnDestroy,
  OnChanges,
  Output,
  DestroyRef
} from '@angular/core';
import { TableUrlInterface } from '../table/models/table-url.interface';
import { ColumnsInterface } from '../table/models/columns.interface';
import { ActionsInterface } from '../table/models/actions.interface';
import { TableOptionsInterface } from '../table/models/options.interface';
import { LanguageService } from '@app/core/language/language.service';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { TableCoreService } from '../table/services/table-core.service';
import { take, debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { BlockItemInterface } from '../table/models/block.interface';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule } from '@angular/forms';
import { EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, TranslateModule, TableModule, CheckboxModule]
})
export class ListComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('actionTpl', { static: true }) actionTpl: TemplateRef<any>;

  @Input() data: any[];
  @Input() selectedData: any[];
  @Input() url: TableUrlInterface;
  @Input() columnsI: ColumnsInterface[];
  @Input() actions: ActionsInterface[];
  @Input() id = 'id';
  @Input() options: TableOptionsInterface;

  @Output() selected: EventEmitter<any[]> = new EventEmitter<any[]>();

  selectedColumns = [] as ColumnsInterface[];
  filteredArray: string[] = [];
  clientSide: boolean;
  listForm: FormGroup;
  columnFormArray: FormArray;
  private firstInit: boolean; // To trigger the first time data load

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    public lang: LanguageService,
    public tableCore: TableCoreService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit() {
    this.selectedColumns = this.columnsI;
    this.updateSelectedColumnsAPI();

    this.getDataFromService();
    this.columnSearchInput();

    this.buildEditableForm();

    this.options = {
      add: true,
      search: true,
      reorder: false,
      loading: false,
      details: true,
      check: false,
      columnSearch: true,
      editable: false,
      ...this.options
    };
  }

  ngOnChanges() {
    if (this.data) {
      this.clientSide = true;
      this.tableCore.pageOptions.count = this.data.length;
    }
  }

  ngOnDestroy() {
    this.tableCore.searchNew$.next({});
    this.tableCore.search = '';
    this.tableCore.pageOptions = {
      count: -1,
      offset: 0,
      limit: 10
    };
    this.firstInit = false;
  }

  /**
   * Get the Table Data from the Service
   * @memberof TableComponent
   */
  getTableData(): void {
    if (this.url && this.url.getAll) {
      this.tableCore
        .getAllData(this.url.getAll)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => (this.data = this.tableCore.tableData));
    }
  }

  getDataFromService() {
    if (!this.clientSide) {
      this.getTableData();
    }
  }

  buildEditableForm() {
    if (!this.options.editable) return;
    // const newFilArray = this.columnsI.map(col => {
    //   if (!col.editable) return;
    //   return { [col.field]: ['', col.form.validation] };
    // });
    this.columnFormArray = this.fb.array([]);
  }

  createRowFormArray() {
    return this.fb.group({});
  }

  /**
   * Navigate to Details Page
   * @param {number} id - Item ID
   * @returns {Promise<boolean>}
   * @memberof TableComponent
   */
  goToDetailsPage(id: string): Promise<boolean> {
    return this.router.navigate(['../details', id], {
      relativeTo: this.route.parent
    });
  }

  /**
   * Navigate to Edit Page
   * @param {number} id - Item ID
   * @returns {Promise<boolean>}
   * @memberof TableComponent
   */
  goToEditPage(id: string): Promise<boolean> {
    return this.router.navigate(['../edit', id], {
      relativeTo: this.route.parent
    });
  }

  /**
   * Create Array of strings from selected columns
   * @memberof ListComponent
   */
  updateSelectedColumnsAPI(): void {
    this.filteredArray = [];
    if (this.selectedColumns && this.selectedColumns.length)
      this.selectedColumns.map(col => this.filteredArray.push(col.field));
  }

  /**
   * Delete a Single Item from the Table
   * @param {string} id - Item ID
   * @memberof TableComponent
   */
  delete(id: string): void {
    if (this.url.delete) {
      this.tableCore
        .deleteItem(this.url.delete, id)
        .pipe(
          take(1),
          finalize(() => this.getTableData()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    }
  }

  export(): void {
    if (this.url.export) {
      this.tableCore
        .exportTable(this.url.export, this.filteredArray)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  /**
   * Block a single Item from the Table
   * @param {BlockItemInterface} data - The item ID and Block State
   * @param {*} item - The full object of the selected item
   * @returns {void}
   * @memberof TableComponent
   */
  toggleBlock(data: BlockItemInterface, item?: any): void {
    if (this.url.block) {
      this.tableCore
        .toggleBlock(this.url.block, data, item)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe();
    }
  }

  /**
   * Fire When The Table Page Changes
   * @param {*} pageInfo - The Object For Table Pages
   * @memberof TableComponent
   */
  setPage(pageInfo: any): void {
    this.tableCore.pageOptions.offset = pageInfo.first / pageInfo.rows;
    this.tableCore.pageOptions.limit = pageInfo.rows;
    // if (!this.data) {
    this.getTableData();
    // }
  }

  /**
   * Search For Each Table Column
   * @param {*} pageInfo - Table Page Info Event
   * @memberof ListComponent
   */
  columnSearchHandle(searchValue: string, colId: string): void {
    this.tableCore.searchNew$.next({
      ...this.tableCore.searchNew$.value,
      [colId]: searchValue
    });
  }

  /**
   * Search Functionality for Each Column with typing
   * Then call the backend to get the matched search criteria
   * @memberof TableComponent
   */
  columnSearchInput(): void {
    this.tableCore.searchNew$
      .pipe(debounceTime(700), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.clientSide) {
          this.firstInit ? this.getTableData() : (this.firstInit = true);
        }
      });
  }

  onRowSelect(event: any) {
    this.selected.emit(this.selectedData);
  }

  onRowUnselect(event: any) {
    this.selected.emit(this.selectedData);
  }
}
